/**
 * Decides when an image is shown as a 360° panorama, and maps the sphere viewer's zoom to the
 * toolbar's percent scale. Pure, so `node --test` covers it.
 *
 * An equirectangular panorama covers 360° × 180°, so its pixels are exactly twice as wide as
 * tall. Ordinary photos are 3:2, 4:3 or 16:9, so a 2:1 image is treated as a panorama; the user
 * can still switch any image between flat and 360° in the toolbar. Cropped panoramas (not 2:1)
 * carry their geometry in XMP `GPano` metadata, which the sphere viewer reads on its own once the
 * 360° view is on.
 */

/** Allowed deviation from 2:1, as a fraction. Cameras sometimes pad a row or two. */
const RATIO_TOLERANCE = 0.02;
/** Below this width a 2:1 image is more likely a banner than a panorama. */
const MIN_PANORAMA_WIDTH = 1024;

/** The sphere viewer's zoom level (0–100) that the toolbar shows as 100%. */
export const PANORAMA_DEFAULT_ZOOM = 50;

/**
 * True when the pixels are 2:1, whatever their size. For thumbnails (scaled copies keep the
 * ratio) and badges; the viewer itself uses `isEquirectangularSize`.
 * @param {number} width
 * @param {number} height
 */
export function isEquirectangularRatio(width, height) {
  const w = Number(width);
  const h = Number(height);
  if (!(w > 0) || !(h > 0)) return false;
  return Math.abs(w / h - 2) <= 2 * RATIO_TOLERANCE;
}

/**
 * True when an image of this pixel size looks like a full equirectangular panorama.
 * @param {number} width
 * @param {number} height
 */
export function isEquirectangularSize(width, height) {
  return Number(width) >= MIN_PANORAMA_WIDTH && isEquirectangularRatio(width, height);
}

/** Sphere zoom level (0–100) to the toolbar's scale, where the default level is 1 (100%). */
export function zoomLevelToScale(level) {
  const value = Number(level);
  if (!Number.isFinite(value)) return 1;
  return Math.max(0, value) / PANORAMA_DEFAULT_ZOOM;
}

/** Toolbar scale to a sphere zoom level, clamped to the viewer's 0–100 range. */
export function scaleToZoomLevel(scale) {
  const value = Number(scale);
  if (!Number.isFinite(value)) return PANORAMA_DEFAULT_ZOOM;
  return Math.min(100, Math.max(0, Math.round(value * PANORAMA_DEFAULT_ZOOM)));
}
